'use client';

import { cloneElement, forwardRef, useCallback, useEffect } from 'react';
import { useInView } from 'react-intersection-observer';
import { useAnalytics } from '../hooks/useAnalytics';
import { getCleanLocationHref } from '../lib/urlUtils';
import { warnOnDevIfCloneElementWillFail } from '../lib/utils';
import { useAnalyticsPageView } from './PageView/Context';

type FeatureViewedProps = {
  activityId?: string;
  entityName: string;
  entitySection: string;
  impressionPosition: number;
  impressionSection?: string;
  waitForAuth?: boolean;
  isLoadingAuth?: boolean;
  additionalAttributes?: object;
  scrollableAncestor?: HTMLElement;
  direction?: 'horizontal' | 'vertical';
  children: React.JSX.Element;
};

export const FeatureViewed = forwardRef(
  (
    {
      activityId,
      entityName,
      entitySection,
      impressionPosition,
      impressionSection,
      waitForAuth,
      isLoadingAuth,
      additionalAttributes,
      scrollableAncestor,
      children,
    }: FeatureViewedProps,
    ref,
  ) => {
    const { analytics, deployableName, deployableVersion, deviceId, gitSha } = useAnalytics();
    const { pageViewId, pageViewReady, pageName } = useAnalyticsPageView();
    const { ref: inViewRef, inView } = useInView({
      root: scrollableAncestor,
      threshold: 0,
      rootMargin: '1px',
      triggerOnce: true,
    });

    const setRefs = useCallback(
      (node: HTMLElement | null) => {
        if (ref && 'current' in ref) {
          ref.current = node;
        }
        inViewRef(node);
      },
      [inViewRef, ref],
    );

    useEffect(() => {
      if (pageViewReady && deviceId && inView && analytics && (!waitForAuth || !isLoadingAuth)) {
        void analytics.track(
          'Feature Viewed',
          {
            activity_id: activityId,
            entity_name: entityName,
            entity_section: entitySection,
            impression_page_name: pageName,
            impression_page_url: getCleanLocationHref(),
            impression_pageview_id: pageViewId,
            impression_position: impressionPosition,
            impression_section: impressionSection,
            deployable_name: deployableName,
            deployable_version: deployableVersion,
            traditional_device_id: deviceId,
            additional_attributes: additionalAttributes,
          },
          {
            context: {
              app: {
                name: deployableName,
                version: gitSha,
              },
            },
          },
        );
      }
    }, [pageViewReady, deviceId, inView, waitForAuth, isLoadingAuth]);

    warnOnDevIfCloneElementWillFail(children, 'FeatureViewed');
    return cloneElement(children, { ref: setRefs });
  },
);
